import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useData } from '../../contexts/DataContext';

const { FiX, FiCheckSquare, FiBriefcase, FiUser, FiCalendar, FiFlag, FiClock, FiRefreshCw } = FiIcons;

const TaskModal = ({ isOpen, onClose, task, initialProjectId = '' }) => {
  const { addTask, updateTask, projects, team } = useData();
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    projectId: '',
    assignee: '',
    status: 'To Do',
    priority: 'Medium',
    dueDate: '',
    estimatedTime: 60,
    recurrence: 'None'
  });

  useEffect(() => {
    if (isOpen) {
      if (task) {
        setFormData({
          ...task,
          dueDate: task.dueDate ? new Date(task.dueDate).toISOString().split('T')[0] : '',
          projectId: task.projectId || ''
        });
      } else {
        setFormData({
          title: '',
          description: '',
          projectId: initialProjectId || '',
          assignee: '',
          status: 'To Do',
          priority: 'Medium',
          dueDate: '',
          estimatedTime: 60,
          recurrence: 'None'
        });
      }
    }
  }, [task, initialProjectId, isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (task) {
      await updateTask(task.id, formData);
    } else {
      await addTask(formData);
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4 z-[60]">
        <motion.div initial={{ opacity: 0, scale: 0.95, y: 20 }} animate={{ opacity: 1, scale: 1, y: 0 }} className="bg-white rounded-[2.5rem] shadow-2xl w-full max-w-xl overflow-hidden">
          <div className="p-8 border-b border-slate-50 flex items-center justify-between bg-slate-50/50">
            <h2 className="text-2xl font-black text-slate-900 tracking-tighter flex items-center gap-3">
              <SafeIcon icon={FiCheckSquare} className="text-blue-600" />
              {task ? 'Update Task' : 'New Task'}
            </h2>
            <button onClick={onClose} className="p-2 hover:bg-white rounded-xl transition-colors">
              <SafeIcon icon={FiX} className="text-slate-400" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="p-8 space-y-6 max-h-[70vh] overflow-y-auto custom-scrollbar">
            <div className="space-y-4">
              <input placeholder="Task Title" className="w-full text-xl font-bold p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none focus:ring-2 focus:ring-blue-500" value={formData.title} onChange={e => setFormData({ ...formData, title: e.target.value })} required />
              <textarea placeholder="Description" rows={3} className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold" value={formData.description} onChange={e => setFormData({ ...formData, description: e.target.value })} />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Link to Project</label>
                <select 
                  className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold"
                  value={formData.projectId}
                  onChange={e => setFormData({ ...formData, projectId: e.target.value })}
                >
                  <option value="">Internal / No Project</option>
                  {projects.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
                </select>
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Assignee</label>
                <select className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold" value={formData.assignee} onChange={e => setFormData({ ...formData, assignee: e.target.value })}>
                  <option value="">Unassigned</option>
                  {team.map(m => <option key={m.id} value={m.name}>{m.name}</option>)}
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Due Date</label>
                <input type="date" className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold" value={formData.dueDate} onChange={e => setFormData({ ...formData, dueDate: e.target.value })} />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase tracking-widest pl-1">Priority</label>
                <select className="w-full p-4 bg-slate-50 rounded-2xl border border-slate-100 outline-none font-bold" value={formData.priority} onChange={e => setFormData({ ...formData, priority: e.target.value })}>
                  <option>Low</option>
                  <option>Medium</option>
                  <option>High</option>
                </select>
              </div>
            </div>

            <button type="submit" className="w-full bg-blue-600 text-white py-5 rounded-2xl font-black shadow-xl hover:bg-blue-700 transition-all">
              {task ? 'Save Changes' : 'Create Task'}
            </button>
          </form>
        </motion.div>
      </div>
    </AnimatePresence>
  );
};

export default TaskModal;